import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader, ConfirmDialog } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { SyutGrid, type SyutGridColumn, type ActionMenuItem, MinimizedModalStrip } from 'components/ui';
import notificationService from '../../../../services/notificationService';
import { userApi, UserDto, CreateUserRequest, UpdateUserRequest, userRateApi, UserRateDto } from 'api/services/timeTrackerApi';
import { useLocalAuth } from 'contexts/LocalAuthContext';
import { useUnsavedChangesGuard } from 'hooks/useUnsavedChangesGuard';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

const fmtMoney = (n: number | null | undefined) =>
  n == null || isNaN(Number(n)) ? '—' : Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const todayIso = () => new Date().toISOString().slice(0, 10);

const blankCreate: CreateUserRequest = {
  username: '', email: '', fullName: '', password: '',
  role: 'User', isActive: true,
};

interface UserMasterScreenProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
}

const UserMasterScreen: React.FC<UserMasterScreenProps> = ({ tabId, moduleName }) => {
  const { user: me } = useLocalAuth();
  const currentUserId = me?.id ?? '';
  const { minimize, isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  const [rows, setRows] = useState<UserDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);

  // Create modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState<CreateUserRequest>(blankCreate);
  const [createSaving, setCreateSaving] = useState(false);
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({});

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState<UserDto | null>(null);
  const [editForm, setEditForm] = useState<UpdateUserRequest>({ email: '', fullName: '', role: 'User', isActive: true });
  const [editSaving, setEditSaving] = useState(false);
  const [editErrors, setEditErrors] = useState<Record<string, string>>({});

  // Reset password modal
  const [resetOpen, setResetOpen] = useState(false);
  const [resetTarget, setResetTarget] = useState<UserDto | null>(null);
  const [resetPwd, setResetPwd] = useState('');
  const [resetConfirm, setResetConfirm] = useState('');
  const [resetSaving, setResetSaving] = useState(false);

  // Delete confirmation
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<UserDto | null>(null);

  // Unsaved-changes guards
  const createGuard = useUnsavedChangesGuard(createOpen, createForm, () => !createSaving && setCreateOpen(false));
  const editGuard   = useUnsavedChangesGuard(editOpen, editForm, () => !editSaving && setEditOpen(false));
  const resetGuard  = useUnsavedChangesGuard(resetOpen, { resetPwd, resetConfirm }, () => !resetSaving && setResetOpen(false));

  // Minimize plumbing — one set of refs per modal
  const createFormRef = useRef(createForm);       createFormRef.current = createForm;
  const editingRef = useRef(editing);             editingRef.current = editing;
  const editFormRef = useRef(editForm);           editFormRef.current = editForm;
  const resetTargetRef = useRef(resetTarget);     resetTargetRef.current = resetTarget;
  const resetFormRef = useRef({ resetPwd, resetConfirm }); resetFormRef.current = { resetPwd, resetConfirm };
  const createSnapshotRef = useRef<string>('');
  const editSnapshotRef   = useRef<string>('');
  const resetSnapshotRef  = useRef<string>('');
  const isCreateRestoreRef = useRef(false);
  const isEditRestoreRef   = useRef(false);
  const isResetRestoreRef  = useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!createOpen) return;
    if (isCreateRestoreRef.current) { isCreateRestoreRef.current = false; return; }
    createSnapshotRef.current = JSON.stringify(createForm);
  }, [createOpen]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!editOpen) return;
    if (isEditRestoreRef.current) { isEditRestoreRef.current = false; return; }
    editSnapshotRef.current = JSON.stringify(editForm);
  }, [editOpen]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!resetOpen) return;
    if (isResetRestoreRef.current) { isResetRestoreRef.current = false; return; }
    resetSnapshotRef.current = JSON.stringify({ resetPwd: '', resetConfirm: '' });
  }, [resetOpen]);

  const handleMinimizeCreate = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentForm = { ...createFormRef.current };
    const snapshot = createSnapshotRef.current;
    const nameStr = currentForm.username?.trim() || currentForm.fullName?.trim();
    const title = nameStr ? `Add - ${nameStr} (1)` : `Add New User (1)`;
    const isDirty = snapshot ? JSON.stringify(currentForm) !== snapshot : false;
    minimize({
      id: `${tabId}-form-modal-create-1`, title, moduleName, tabId, isDirty,
      onRestore: () => {
        isCreateRestoreRef.current = true;
        setCreateForm(currentForm);
        createSnapshotRef.current = snapshot;
        setCreateOpen(true);
      },
    });
    setCreateOpen(false);
  }, [tabId, moduleName, minimize]);

  const handleMinimizeEdit = useCallback(() => {
    if (!tabId || !moduleName) return;
    const currentEditing = editingRef.current;
    if (!currentEditing) return;
    const currentForm = { ...editFormRef.current };
    const snapshot = editSnapshotRef.current;
    const title = `Edit - ${currentEditing.username}`;
    const isDirty = snapshot ? JSON.stringify(currentForm) !== snapshot : false;
    minimize({
      id: `${tabId}-form-modal-edit-${currentEditing.id}`, title, moduleName, tabId, isDirty,
      onRestore: () => {
        isEditRestoreRef.current = true;
        setEditing(currentEditing);
        setEditForm(currentForm);
        editSnapshotRef.current = snapshot;
        setEditOpen(true);
      },
    });
    setEditOpen(false);
  }, [tabId, moduleName, minimize]);

  const handleMinimizeReset = useCallback(() => {
    if (!tabId || !moduleName) return;
    const target = resetTargetRef.current;
    if (!target) return;
    const currentForm = { ...resetFormRef.current };
    const snapshot = resetSnapshotRef.current;
    const title = `Reset password - ${target.username}`;
    const isDirty = snapshot ? JSON.stringify(currentForm) !== snapshot : false;
    minimize({
      id: `${tabId}-form-modal-reset-${target.id}`, title, moduleName, tabId, isDirty,
      onRestore: () => {
        isResetRestoreRef.current = true;
        setResetTarget(target);
        setResetPwd(currentForm.resetPwd);
        setResetConfirm(currentForm.resetConfirm);
        resetSnapshotRef.current = snapshot;
        setResetOpen(true);
      },
    });
    setResetOpen(false);
  }, [tabId, moduleName, minimize]);

  // Rates modal
  const [ratesOpen, setRatesOpen]         = useState(false);
  const [ratesUser, setRatesUser]         = useState<UserDto | null>(null);
  const [ratesData, setRatesData]         = useState<UserRateDto[]>([]);
  const [ratesLoading, setRatesLoading]   = useState(false);
  const [newRate, setNewRate]             = useState<string>('');           // string for input
  const [newRateFrom, setNewRateFrom]     = useState<string>(todayIso());
  const [newRateNote, setNewRateNote]     = useState<string>('');
  const [addingRate, setAddingRate]       = useState(false);
  const [pendingRateDelete, setPendingRateDelete] = useState<UserRateDto | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await userApi.list({
        page: page + 1,
        pageSize,
        searchTerm: searchQuery || undefined,
        includeInactive: includeInactive || undefined,
      });
      setRows(res.items);
      setTotalCount(res.totalCount);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchQuery, includeInactive]);

  useEffect(() => {
    const t = setTimeout(() => { load(); }, 350);
    return () => clearTimeout(t);
  }, [load]);

  // ── Create ──────────────────────────────────────────────────────────
  const openCreate = useCallback(() => {
    const modalId = tabId ? `${tabId}-form-modal-create-1` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setCreateForm(blankCreate);
    setCreateErrors({});
    setCreateOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const submitCreate = async () => {
    const errs: Record<string, string> = {};
    if (!createForm.username.trim() || createForm.username.length < 3) errs.username = 'Username (3+ chars) is required';
    if (!/.+@.+\..+/.test(createForm.email)) errs.email = 'Valid email is required';
    if (!createForm.fullName.trim() || createForm.fullName.length < 2) errs.fullName = 'Full name is required';
    if (!createForm.password || createForm.password.length < 6) errs.password = 'Password (6+ chars) is required';
    setCreateErrors(errs);
    if (Object.keys(errs).length) return;

    setCreateSaving(true);
    try {
      const created = await userApi.create(createForm);
      notificationService.toast.success(`User "${created.username}" created.`);
      setCreateOpen(false);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Create failed.');
    } finally {
      setCreateSaving(false);
    }
  };

  // ── Edit ────────────────────────────────────────────────────────────
  const openEdit = useCallback((row: UserDto) => {
    const modalId = tabId ? `${tabId}-form-modal-edit-${row.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setEditing(row);
    setEditForm({
      email: row.email,
      fullName: row.fullName,
      role: (row.role === 'Admin' ? 'Admin' : 'User'),
      isActive: row.isActive,
    });
    setEditErrors({});
    setEditOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const submitEdit = async () => {
    if (!editing) return;
    const errs: Record<string, string> = {};
    if (!/.+@.+\..+/.test(editForm.email)) errs.email = 'Valid email is required';
    if (!editForm.fullName.trim() || editForm.fullName.length < 2) errs.fullName = 'Full name is required';
    setEditErrors(errs);
    if (Object.keys(errs).length) return;

    setEditSaving(true);
    try {
      await userApi.update(editing.id, editForm);
      notificationService.toast.success('User updated.');
      setEditOpen(false);
      setEditing(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Update failed.');
    } finally {
      setEditSaving(false);
    }
  };

  // ── Reset password ─────────────────────────────────────────────────
  const openReset = useCallback((row: UserDto) => {
    const modalId = tabId ? `${tabId}-form-modal-reset-${row.id}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    setResetTarget(row);
    setResetPwd('');
    setResetConfirm('');
    setResetOpen(true);
  }, [tabId, isMinimized, dismissMinimized, getMinimizedModal]);

  const submitReset = async () => {
    if (!resetTarget) return;
    if (resetPwd.length < 6) {
      notificationService.toast.error('Password must be at least 6 characters.');
      return;
    }
    if (resetPwd !== resetConfirm) {
      notificationService.toast.error("Passwords don't match.");
      return;
    }
    setResetSaving(true);
    try {
      await userApi.resetPassword(resetTarget.id, resetPwd);
      notificationService.toast.success(`Password reset for "${resetTarget.username}".`);
      setResetOpen(false);
      setResetTarget(null);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Reset failed.');
    } finally {
      setResetSaving(false);
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────
  const requestDelete = useCallback((row: UserDto) => {
    setPendingDelete(row);
    setDeleteOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await userApi.remove(pendingDelete.id);
      notificationService.toast.success(`"${pendingDelete.username}" deleted.`);
      setDeleteOpen(false);
      setPendingDelete(null);
      await load();
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  }, [pendingDelete, load]);

  // ── Rates ──────────────────────────────────────────────────────────
  const openRates = useCallback(async (row: UserDto) => {
    setRatesUser(row);
    setRatesOpen(true);
    setRatesLoading(true);
    setRatesData([]);
    setNewRate('');
    setNewRateFrom(todayIso());
    setNewRateNote('');
    try {
      const list = await userRateApi.list(row.id);
      setRatesData(list);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Failed to load rates.');
    } finally {
      setRatesLoading(false);
    }
  }, []);

  const addRate = async () => {
    if (!ratesUser) return;
    const amt = parseFloat(newRate);
    if (isNaN(amt) || amt < 0) {
      notificationService.toast.error('Enter a non-negative hourly rate.');
      return;
    }
    if (!newRateFrom) {
      notificationService.toast.error('Pick an effective-from date.');
      return;
    }
    setAddingRate(true);
    try {
      await userRateApi.create(ratesUser.id, {
        hourlyRate: amt,
        effectiveFrom: newRateFrom,
        note: newRateNote || null,
      });
      notificationService.toast.success(`Rate added for ${ratesUser.fullName}.`);
      // Refresh rates list AND the user list (so currentHourlyRate column updates)
      const [list] = await Promise.all([userRateApi.list(ratesUser.id), load()]);
      setRatesData(list);
      setNewRate('');
      setNewRateNote('');
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Add failed.');
    } finally {
      setAddingRate(false);
    }
  };

  const confirmDeleteRate = async () => {
    if (!ratesUser || !pendingRateDelete) return;
    try {
      await userRateApi.remove(ratesUser.id, pendingRateDelete.id, pendingRateDelete.rowVersion);
      notificationService.toast.success('Rate deleted.');
      setPendingRateDelete(null);
      const [list] = await Promise.all([userRateApi.list(ratesUser.id), load()]);
      setRatesData(list);
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || 'Delete failed.');
    }
  };

  // ── Grid ───────────────────────────────────────────────────────────
  type GridRow = UserDto & { id: string };
  const columns: SyutGridColumn<GridRow>[] = useMemo(() => [
    { id: 'username',   header: 'Username',   accessorKey: 'username',   size: 180 },
    { id: 'fullName',   header: 'Full Name',  accessorKey: 'fullName',   size: 220 },
    { id: 'email',      header: 'Email',      accessorKey: 'email',      size: 240 },
    { id: 'role',       header: 'Role',       accessorKey: 'role',       size: 110,
      cell: ({ getValue }) => {
        const r = getValue() as string;
        return (
          <span className="badge" style={{ background: r === 'Admin' ? '#fef3c7' : '#dbeafe', color: r === 'Admin' ? '#92400e' : '#1e3a8a' }}>
            {r}
          </span>
        );
      },
    },
    { id: 'currentHourlyRate', header: 'Rate / hr', accessorKey: 'currentHourlyRate', size: 110,
      cell: ({ getValue }) => {
        const v = getValue() as number | null | undefined;
        return v == null
          ? <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>Not set</span>
          : <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtMoney(v)}</span>;
      },
    },
    { id: 'isActive',   header: 'Status',     accessorKey: 'isActive',   size: 100,
      cell: ({ getValue }) => (
        <span className={`badge ${getValue() ? 'badge-active' : 'badge-inactive'}`}>
          {getValue() ? 'Active' : 'Inactive'}
        </span>
      ),
    },
    { id: 'lastLoginAt', header: 'Last login', accessorKey: 'lastLoginAt', size: 160,
      cell: ({ getValue }) => {
        const v = getValue() as string | null | undefined;
        return v ? new Date(v).toLocaleString() : '—';
      },
    },
  ], []);

  const rowActions = useCallback((row: GridRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [
      { id: 'edit',  label: 'Edit',           icon: <IconSystem.edit         size={16} />, onClick: () => openEdit(row) },
      { id: 'rates', label: 'Manage rates',   icon: <IconSystem.dollarSign  size={16} />, onClick: () => openRates(row) },
      { id: 'reset', label: 'Reset password', icon: <IconSystem.lock         size={16} />, onClick: () => openReset(row) },
    ];
    if (row.id !== currentUserId) {
      items.push({ id: 'delete', label: 'Delete', icon: <IconSystem.trash size={16} />, onClick: () => requestDelete(row), variant: 'danger' });
    }
    return items;
  }, [currentUserId, openEdit, openReset, openRates, requestDelete]);

  const actions: ActionButton[] = [
    { id: 'add',     icon: <IconSystem.add     size={18} />, label: 'Add User', onClick: openCreate, tooltip: 'Add new user', variant: 'icon' },
    { id: 'refresh', icon: <IconSystem.refresh size={18} />, label: 'Refresh',  onClick: load,       tooltip: 'Refresh',       variant: 'icon' },
  ];

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="module-master-container">
      <ModuleHeader
        title="User Master"
        description="Manage who can log in. Only admins can create or edit users."
        icon={<IconSystem.user size={18} />}
        searchConfig={{ value: searchQuery, onChange: setSearchQuery, placeholder: 'Search by username, email or name…' }}
        filterConfig={{ onClick: () => setFilterOpen(o => !o), isActive: filterOpen || includeInactive }}
        actions={actions}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        <div className="data-grid-container">
          <SyutGrid
            data={rows as GridRow[]}
            columns={columns}
            enableRowSelection
            enablePagination
            manualPagination
            totalCount={totalCount}
            currentPage={page}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={(s) => { setPageSize(s); setPage(0); }}
            rowActions={rowActions}
            emptyMessage={loading ? 'Loading…' : 'No users found.'}
          />
        </div>

        {filterOpen && (
          <div className="search-panel">
            <div className="search-panel-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                <IconSystem.filter size={14} />
                <h3 className="search-panel-title">Filters</h3>
              </div>
              <button onClick={() => setFilterOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.25rem', display: 'flex', color: 'var(--text-muted)' }}>
                <IconSystem.x size={16} />
              </button>
            </div>
            <div className="search-panel-content">
              <div className="filter-section">
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.85rem' }}>
                  <input
                    type="checkbox"
                    checked={includeInactive}
                    onChange={(e) => { setIncludeInactive(e.target.checked); setPage(0); }}
                  />
                  Include inactive users
                </label>
              </div>
            </div>
            <div className="filter-actions">
              <button className="btn btn-secondary" onClick={() => { setIncludeInactive(false); setPage(0); }}>Clear</button>
              <button className="btn btn-primary" onClick={() => setFilterOpen(false)}>Done</button>
            </div>
          </div>
        )}
      </div>

      {/* Create modal */}
      {createOpen && (
        <div className="modal-backdrop" onClick={createGuard.requestClose}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.user size={16} style={{ marginRight: 8 }} />
                New User
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimizeCreate} title="Minimize to bar" disabled={createSaving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={createGuard.requestClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Username<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <input
                    className={`form-input ${createErrors.username ? 'form-field-error' : ''}`}
                    value={createForm.username}
                    onChange={(e) => setCreateForm({ ...createForm, username: e.target.value })}
                    maxLength={100}
                    autoFocus
                  />
                  {createErrors.username && <div className="form-error-message">{createErrors.username}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Email<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <input
                    className={`form-input ${createErrors.email ? 'form-field-error' : ''}`}
                    type="email"
                    value={createForm.email}
                    onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                    maxLength={255}
                  />
                  {createErrors.email && <div className="form-error-message">{createErrors.email}</div>}
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Full name<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className={`form-input ${createErrors.fullName ? 'form-field-error' : ''}`}
                  value={createForm.fullName}
                  onChange={(e) => setCreateForm({ ...createForm, fullName: e.target.value })}
                  maxLength={200}
                />
                {createErrors.fullName && <div className="form-error-message">{createErrors.fullName}</div>}
              </div>

              <div className="form-grid-2" style={{ marginTop: '0.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={createForm.role}
                    onChange={(e) => setCreateForm({ ...createForm, role: e.target.value as 'Admin' | 'User' })}
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={createForm.isActive ? '1' : '0'}
                    onChange={(e) => setCreateForm({ ...createForm, isActive: e.target.value === '1' })}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Password<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className={`form-input ${createErrors.password ? 'form-field-error' : ''}`}
                  type="password"
                  value={createForm.password}
                  onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                  autoComplete="new-password"
                />
                {createErrors.password && <div className="form-error-message">{createErrors.password}</div>}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={createGuard.requestClose} disabled={createSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={submitCreate} disabled={createSaving}>
                {createSaving ? 'Creating…' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit modal */}
      {editOpen && editing && (
        <div className="modal-backdrop" onClick={editGuard.requestClose}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.edit size={16} style={{ marginRight: 8 }} />
                Edit User · {editing.username}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimizeEdit} title="Minimize to bar" disabled={editSaving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={editGuard.requestClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input className="form-input" value={editing.username} disabled />
              </div>

              <div className="form-grid-2" style={{ marginTop: '0.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Email<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <input
                    className={`form-input ${editErrors.email ? 'form-field-error' : ''}`}
                    type="email"
                    value={editForm.email}
                    onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    maxLength={255}
                  />
                  {editErrors.email && <div className="form-error-message">{editErrors.email}</div>}
                </div>
                <div className="form-group">
                  <label className="form-label">Full name<span style={{ color: 'var(--danger)' }}> *</span></label>
                  <input
                    className={`form-input ${editErrors.fullName ? 'form-field-error' : ''}`}
                    value={editForm.fullName}
                    onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                    maxLength={200}
                  />
                  {editErrors.fullName && <div className="form-error-message">{editErrors.fullName}</div>}
                </div>
              </div>

              <div className="form-grid-2" style={{ marginTop: '0.875rem' }}>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={editForm.role}
                    onChange={(e) => setEditForm({ ...editForm, role: e.target.value as 'Admin' | 'User' })}
                    disabled={editing.id === currentUserId}
                  >
                    <option value="User">User</option>
                    <option value="Admin">Admin</option>
                  </select>
                  {editing.id === currentUserId && (
                    <div className="form-field-help" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
                      You can't change your own role.
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Status</label>
                  <select
                    className="form-select"
                    value={editForm.isActive ? '1' : '0'}
                    onChange={(e) => setEditForm({ ...editForm, isActive: e.target.value === '1' })}
                    disabled={editing.id === currentUserId}
                  >
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                  {editing.id === currentUserId && (
                    <div className="form-field-help" style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>
                      You can't deactivate your own account.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={editGuard.requestClose} disabled={editSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={submitEdit} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset password modal */}
      {resetOpen && resetTarget && (
        <div className="modal-backdrop" onClick={resetGuard.requestClose}>
          <div className="modal-container modal-sm" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.lock size={16} style={{ marginRight: 8 }} />
                Reset password · {resetTarget.username}
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                {tabId && moduleName && (
                  <button className="modal-header-btn" onClick={handleMinimizeReset} title="Minimize to bar" disabled={resetSaving}>
                    <IconSystem.chevronDown size={16} />
                  </button>
                )}
                <button className="modal-close" onClick={resetGuard.requestClose}>
                  <IconSystem.x size={18} />
                </button>
              </div>
            </div>
            <div className="modal-content">
              <div className="form-group">
                <label className="form-label">New password<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className="form-input"
                  type="password"
                  value={resetPwd}
                  onChange={(e) => setResetPwd(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                />
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: 4 }}>6+ characters</div>
              </div>
              <div className="form-group" style={{ marginTop: '0.875rem' }}>
                <label className="form-label">Confirm new password<span style={{ color: 'var(--danger)' }}> *</span></label>
                <input
                  className={`form-input ${resetConfirm && resetConfirm !== resetPwd ? 'form-field-error' : ''}`}
                  type="password"
                  value={resetConfirm}
                  onChange={(e) => setResetConfirm(e.target.value)}
                  autoComplete="new-password"
                />
                {resetConfirm && resetConfirm !== resetPwd && (
                  <div className="form-error-message">Passwords don't match</div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={resetGuard.requestClose} disabled={resetSaving}>Cancel</button>
              <button className="btn btn-primary" onClick={submitReset} disabled={resetSaving || resetPwd.length < 6 || resetPwd !== resetConfirm}>
                {resetSaving ? 'Saving…' : 'Reset password'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={deleteOpen}
        title="Delete User"
        message={`Delete user "${pendingDelete?.username}"? They will no longer be able to log in. This is a soft delete; data they created is preserved.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        loading={deleting}
        loadingMessage="Deleting…"
        onConfirm={confirmDelete}
        onCancel={() => { setDeleteOpen(false); setPendingDelete(null); }}
      />

      <ConfirmDialog
        open={createGuard.discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes to this user. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={createGuard.confirmDiscard}
        onCancel={createGuard.cancelDiscard}
      />

      <ConfirmDialog
        open={editGuard.discardOpen}
        title="Discard Changes?"
        message="You have unsaved changes to this user. Do you want to discard them?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={editGuard.confirmDiscard}
        onCancel={editGuard.cancelDiscard}
      />

      <ConfirmDialog
        open={resetGuard.discardOpen}
        title="Discard Changes?"
        message="You have entered a new password. Do you want to discard it?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={resetGuard.confirmDiscard}
        onCancel={resetGuard.cancelDiscard}
      />

      {/* Manage Rates modal */}
      {ratesOpen && ratesUser && (
        <div className="modal-backdrop" onClick={() => setRatesOpen(false)}>
          <div className="modal-container modal-md" onClick={e => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="modal-header">
              <h2 className="modal-title">
                <IconSystem.dollarSign size={16} style={{ marginRight: 8 }} />
                Rates — {ratesUser.fullName}
              </h2>
              <button className="modal-close" onClick={() => setRatesOpen(false)}>
                <IconSystem.x size={18} />
              </button>
            </div>
            <div className="modal-content">
              <div style={{
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '0.5rem',
                padding: '0.75rem',
                marginBottom: '1rem',
              }}>
                <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                  Add a new rate
                </div>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ minWidth: 120 }}>
                    <label className="form-label">Hourly rate</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      className="form-input"
                      value={newRate}
                      onChange={(e) => setNewRate(e.target.value)}
                      placeholder="50.00"
                    />
                  </div>
                  <div style={{ minWidth: 140 }}>
                    <label className="form-label">Effective from</label>
                    <input
                      type="date"
                      className="form-input"
                      value={newRateFrom}
                      onChange={(e) => setNewRateFrom(e.target.value)}
                    />
                  </div>
                  <div style={{ flex: 1, minWidth: 180 }}>
                    <label className="form-label">Note (optional)</label>
                    <input
                      className="form-input"
                      value={newRateNote}
                      onChange={(e) => setNewRateNote(e.target.value)}
                      placeholder="Annual raise, promotion, etc."
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={addRate}
                    disabled={addingRate || !newRate || !newRateFrom}
                  >
                    {addingRate ? 'Adding…' : 'Add'}
                  </button>
                </div>
                <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.375rem' }}>
                  Adding a new rate auto-closes any current open rate the day before this one starts.
                </div>
              </div>

              <div style={{ fontSize: '0.8125rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-primary)' }}>
                Rate history
              </div>
              {ratesLoading ? (
                <div style={{ padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>Loading…</div>
              ) : ratesData.length === 0 ? (
                <div style={{ padding: '0.75rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
                  No rates set yet. Reports will show 0 for this user's hours until you add a rate.
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {ratesData.map(r => {
                    const isOpen = !r.effectiveTo;
                    return (
                      <div key={r.id} style={{
                        display: 'flex', alignItems: 'center', gap: '0.625rem',
                        padding: '0.5rem 0.75rem',
                        border: '1px solid var(--border-color)',
                        borderRadius: '0.375rem',
                        background: isOpen ? 'var(--bg-primary)' : 'var(--bg-secondary)',
                      }}>
                        <span style={{
                          flexShrink: 0,
                          width: 8, height: 8, borderRadius: '50%',
                          background: isOpen ? 'var(--primary)' : 'var(--text-muted)',
                        }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                            {fmtMoney(r.hourlyRate)} / hr
                            {isOpen && <span style={{ marginLeft: '0.5rem', fontSize: '0.7rem', color: 'var(--primary)', fontWeight: 700 }}>CURRENT</span>}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                            {r.effectiveFrom.slice(0, 10)} → {r.effectiveTo ? r.effectiveTo.slice(0, 10) : 'open'}
                            {r.createdByName ? ` · added by ${r.createdByName}` : ''}
                          </div>
                          {r.note && (
                            <div style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginTop: '0.125rem' }}>{r.note}</div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setPendingRateDelete(r)}
                          title="Delete rate"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', padding: '0.25rem' }}
                        >
                          <IconSystem.trash size={14} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setRatesOpen(false)}>Close</button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!pendingRateDelete}
        title="Delete Rate"
        message={pendingRateDelete
          ? `Delete the ${fmtMoney(pendingRateDelete.hourlyRate)}/hr rate effective from ${pendingRateDelete.effectiveFrom.slice(0,10)}? Any past reports that used this rate will recompute with 0 for affected dates.`
          : ''}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        onConfirm={confirmDeleteRate}
        onCancel={() => setPendingRateDelete(null)}
      />
    </div>
  );
};

export default UserMasterScreen;
